import { useState, useEffect, useRef } from 'react';
import * as OTPAuth from 'otpauth';
import { Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * Live TOTP widget — auto-refreshes every 30 sec, circular countdown, copy on click.
 * Props: secret (Base32). Renders nothing if secret missing.
 */
export default function TOTPWidget({ secret, label = 'TOTP' }) {
  const [code, setCode] = useState('------');
  const [remaining, setRemaining] = useState(30);
  const totpRef = useRef(null);

  useEffect(() => {
    if (!secret) { setCode('------'); return; }
    try {
      const cleaned = String(secret).replace(/\s+/g, '').toUpperCase();
      totpRef.current = new OTPAuth.TOTP({
        issuer: 'AxialUp',
        label: 'Booking',
        algorithm: 'SHA1', digits: 6, period: 30,
        secret: OTPAuth.Secret.fromBase32(cleaned),
      });
    } catch (e) { setCode('ERROR'); return; }

    const tick = () => {
      try {
        const c = totpRef.current.generate();
        setCode(c);
        const left = 30 - (Math.floor(Date.now() / 1000) % 30);
        setRemaining(left);
      } catch (e) { setCode('ERROR'); }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [secret]);

  const copy = () => {
    if (!secret || code === 'ERROR') return;
    navigator.clipboard.writeText(code);
    toast.success(`${label} copied: ${code}`);
  };

  const ringColor = remaining <= 5 ? 'border-red-500 animate-pulse' : remaining <= 10 ? 'border-amber-500' : 'border-emerald-500';

  return (
    <button onClick={copy} disabled={!secret} className="inline-flex items-center gap-2 px-3 py-1.5 border-2 rounded font-mono text-base tracking-widest hover:bg-gray-50 disabled:opacity-40" style={{ minWidth: '120px' }} data-testid="totp-widget">
      <div className={`w-7 h-7 rounded-full border-4 ${ringColor} flex items-center justify-center text-[10px] font-bold`}>
        {remaining}
      </div>
      <span className="font-bold">{code}</span>
      <Copy size={14} className="text-gray-500" />
    </button>
  );
}
